/**
 * NmbrsUnlinkedPanel Component
 *
 * Shows Nmbrs employees that are NOT yet linked to an HR-portal employee.
 * Allows quick linking by selecting an HR-portal employee for each.
 * Story 1.3: Ongekoppelde Nmbrs-medewerkers Overzicht
 */

import { useState } from 'react';
import { useUnlinkedNmbrsEmployees, useLinkEmployee, useLinkedEmployees } from '../hooks/useNmbrs';
import { useEmployees } from '../../employees/useEmployees';

export function NmbrsUnlinkedPanel() {
  const { data: unlinkedData, isLoading, isError, error, refetch } = useUnlinkedNmbrsEmployees();
  const { data: employeesData } = useEmployees();
  const { data: linksData } = useLinkedEmployees();
  const linkMutation = useLinkEmployee();

  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [search, setSearch] = useState('');

  const unlinked = unlinkedData?.data ?? [];
  const employees = employeesData?.data ?? [];
  const links = linksData?.data ?? [];
  // M3: Filter out already-linked HR-portal employees from the dropdown
  const linkedEmployeeIds = new Set(links.map((l) => l.employeeId));
  const availableEmployees = employees.filter((e) => !linkedEmployeeIds.has(e.id));

  const filtered = search
    ? unlinked.filter((emp) =>
        emp.displayName.toLowerCase().includes(search.toLowerCase()) ||
        emp.emailWork?.toLowerCase().includes(search.toLowerCase()) ||
        String(emp.id).includes(search)
      )
    : unlinked;

  const handleQuickLink = async (nmbrsEmployeeId: number) => {
    const empId = parseInt(selectedEmployeeId, 10);
    if (isNaN(empId) || empId <= 0) return;

    try {
      await linkMutation.mutateAsync({ employeeId: empId, nmbrsEmployeeId });
      setLinkingId(null);
      setSelectedEmployeeId('');
    } catch {
      // Error handled by mutation state
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5A7A]"></div>
        <span className="ml-2 text-sm text-gray-500">Nmbrs medewerkers ophalen...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">
          {error instanceof Error ? error.message : 'Kan Nmbrs medewerkers niet ophalen'}
        </p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-sm text-[#54949E] hover:text-[#2F5A7A]"
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1E293B]">Ongekoppelde Nmbrs Medewerkers</h3>
          <p className="text-sm text-gray-500">
            {unlinked.length} medewerker{unlinked.length !== 1 ? 's' : ''} in Nmbrs zonder koppeling
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm text-[#54949E] border border-[#E2E8F0] rounded-lg hover:bg-gray-50"
        >
          Vernieuwen
        </button>
      </div>

      {/* Search */}
      {unlinked.length > 5 && (
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op naam, e-mail of ID..."
          className="w-full md:w-80 px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
        />
      )}

      {/* Table */}
      {filtered.length > 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-[#E2E8F0]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nmbrs ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Naam</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {filtered.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{emp.id}</td>
                  <td className="px-4 py-3 text-sm text-[#1E293B]">{emp.displayName}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{emp.emailWork ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {linkingId === emp.id ? (
                      <div className="flex justify-end items-center gap-2">
                        <select
                          value={selectedEmployeeId}
                          onChange={(e) => setSelectedEmployeeId(e.target.value)}
                          className="px-2 py-1 border border-[#E2E8F0] rounded text-sm focus:ring-2 focus:ring-[#54949E]"
                        >
                          <option value="">Selecteer...</option>
                          {availableEmployees.map((e) => (
                            <option key={e.id} value={e.id}>{e.name}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleQuickLink(emp.id)}
                          disabled={!selectedEmployeeId || linkMutation.isPending}
                          className="px-2 py-1 text-xs bg-[#2F5A7A] text-white rounded hover:bg-[#254963] disabled:opacity-50"
                        >
                          {linkMutation.isPending ? '...' : 'Koppel'}
                        </button>
                        <button
                          onClick={() => { setLinkingId(null); setSelectedEmployeeId(''); }}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setLinkingId(emp.id)}
                        className="px-3 py-1 text-sm text-[#54949E] hover:text-[#2F5A7A]"
                      >
                        Koppelen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : unlinked.length > 0 ? (
        <div className="bg-gray-50 border border-[#E2E8F0] rounded-lg p-6 text-center">
          <p className="text-gray-500 text-sm">Geen resultaten voor "{search}"</p>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <p className="text-green-700 text-sm">Alle Nmbrs medewerkers zijn gekoppeld!</p>
        </div>
      )}

      {linkMutation.isError && (
        <p className="text-sm text-red-600">
          {linkMutation.error instanceof Error ? linkMutation.error.message : 'Koppeling mislukt'}
        </p>
      )}
    </div>
  );
}
